// WhyReminissing — universal bridge section that sits BETWEEN Lea's intro
// and either the John or Ella demo. The "life is busy → memories lost →
// AI with a heartbeat" arc, written tight, the same on both pathways.
import { motion } from "framer-motion";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";

const FADE = {
    initial: { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export function WhyReminissing() {
    return (
        <section className="relative py-28 px-6 bg-[#FDFBF7]" data-testid="why-reminissing">
            <div className="max-w-4xl mx-auto text-center">
                <motion.div {...FADE} className="flex justify-center mb-8">
                    <ForgetMeNotIcon size={56} />
                </motion.div>

                <motion.h2
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.08 }}
                    className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#2D2B2A] leading-tight"
                >
                    Life is busy. Often hectic.<br/>
                    <em className="italic text-[#4A5A95]">Memories get lost, or simply forgotten —</em>
                    <br/>and we regret it later.
                </motion.h2>

                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.18 }}
                    className="mt-10 space-y-5 text-[#5C5A59] text-lg leading-relaxed font-light max-w-2xl mx-auto"
                >
                    <p>
                        For decades the only way to assemble a lifetime of memories was to sit with shoeboxes of photos, hand-written letters, and a fading parent's recollection — and hope you got there in time.
                    </p>
                    <p>
                        remembr is built on a cutting-edge form of AI that's only been possible in the last year or two. It quietly assembles and delivers your family's memories on demand — narrated by the people who were actually in the photographs.
                    </p>
                    <p className="text-[#2D2B2A] font-serif italic text-xl sm:text-2xl text-balance">
                        It doesn't just recall a memory — <em className="not-italic">it places you back inside it.</em>
                    </p>
                </motion.div>

                <motion.p
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.28 }}
                    className="mt-12 font-serif italic text-2xl sm:text-3xl text-[#4A5A95]"
                    data-testid="ai-heartbeat-line"
                >
                    This is AI with a heartbeat.
                </motion.p>
            </div>
        </section>
    );
}
